import React from "react";
import {
  CheckCircle2,
  Link,
  Edit3,
  Plus,
  ChevronRight,
  Image as ImageIcon,
  Square
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AutocompleteInput from "@/components/common/AutocompleteInput";
import MatchConfidenceBadge from "@/components/common/MatchConfidenceBadge";
import CreatableSelect from "@/components/common/CreatableSelect";
import { Checkbox } from "@/components/ui/checkbox";

const DEFAULT_COLORS = ["Blanco", "Negro", "Gris", "Madera", "Bronce", "Natural", "Champagne", "Otro"];
const mergeOptions = (...lists) => [...new Set(lists.flat().filter(Boolean))];

// Local text state that commits to the parent after a short pause (avoids re-rendering the whole app per keystroke)
const useDebouncedField = (value, onCommit, delay = 250) => {
  const [text, setText] = React.useState(value || "");
  React.useEffect(() => { setText(value || ""); }, [value]);
  React.useEffect(() => {
    if (text === (value || "")) return undefined;
    const id = setTimeout(() => onCommit(text), delay);
    return () => clearTimeout(id);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return [text, setText, () => { if (text !== (value || "")) onCommit(text); }];
};

const DebouncedInput = ({ value, onCommit, delay = 250, ...props }) => {
  const [text, setText, flush] = useDebouncedField(value, onCommit, delay);
  return <Input {...props} value={text} onChange={(e) => setText(e.target.value)} onBlur={flush} />;
};

const DebouncedAutocomplete = ({ value, onCommit, delay = 250, ...props }) => {
  const [text, setText, flush] = useDebouncedField(value, onCommit, delay);
  return <AutocompleteInput {...props} value={text} onChange={(e) => setText(e.target.value)} onBlur={flush} />;
};

// Stable wrappers so memoized rows don't re-render when parent callbacks are recreated
const useStableCallbacks = (fns) => {
  const ref = React.useRef(fns);
  ref.current = fns;
  return React.useMemo(
    () => Object.fromEntries(Object.keys(fns).map((k) => [k, (...args) => ref.current[k]?.(...args)])),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency in USD
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

// Image Thumbnail Component
const ImageThumbnail = ({ src, alt, className, onClick }) => {
  const [hasError, setHasError] = React.useState(false);
  
  if (!src || hasError) {
    return (
      <div className={`bg-slate-100 flex items-center justify-center ${className}`}>
        <Square className="w-4 h-4 text-slate-300" />
      </div>
    );
  }
  
  return (
    <div 
      className={`relative cursor-pointer group ${className}`}
      onClick={onClick}
    >
      <img 
        src={src} 
        alt={alt} 
        className="w-full h-full object-contain"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

/**
 * ParsedItemsModal - Modal for reviewing and editing parsed quote items before saving (Sales)
 */
const BulkQuickActions = React.memo(({
  parsedItems, selectedIds, hasSelection, allSelected, toggleAll, clearSelection,
  materials, colorOptions, aluminumSeries, glassOptions, allTypeNames, createCatalogValue,
  bulkMaterial, bulkColor, bulkSeries, bulkGlass, bulkName,
}) => {
  const [bulkGlassText, setBulkGlassText] = React.useState("");
  const [bulkNameText, setBulkNameText] = React.useState("");
  const bulkPlaceholder = hasSelection ? `Aplicar a ${selectedIds.size}` : "Aplicar a todos";
  return (
    <div className="flex flex-wrap items-center gap-4 py-2 border-b pb-3" data-testid="parsed-items-quick-actions">
      <div className="flex items-center gap-2 text-sm">
        <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="parsed-select-all" />
        <span className={hasSelection ? "font-medium text-blue-700" : "text-slate-500"} data-testid="parsed-selection-summary">
          {hasSelection ? `${selectedIds.size} seleccionado${selectedIds.size === 1 ? "" : "s"}` : "Seleccionar filas"}
        </span>
        {hasSelection && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearSelection} data-testid="parsed-clear-selection">
            Limpiar
          </Button>
        )}
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 font-medium">Material:</span>
        <div className="w-[150px]">
          <CreatableSelect
            value=""
            onChange={bulkMaterial}
            options={materials}
            placeholder={bulkPlaceholder}
            createLabel="Nuevo material…"
            onCreate={(name) => createCatalogValue("material", name)}
            testId="bulk-material-select"
          />
        </div>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 font-medium">Color:</span>
        <div className="w-[150px]">
          <CreatableSelect
            value=""
            onChange={bulkColor}
            options={colorOptions}
            placeholder={bulkPlaceholder}
            createLabel="Nuevo color…"
            onCreate={(name) => createCatalogValue("color", name)}
            testId="bulk-color-select"
          />
        </div>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 font-medium">Serie:</span>
        <div className="w-[150px]">
          <CreatableSelect
            value=""
            onChange={bulkSeries}
            options={aluminumSeries}
            placeholder={bulkPlaceholder}
            createLabel="Nueva serie…"
            onCreate={(name) => createCatalogValue("series", name)}
            testId="bulk-series-select"
          />
        </div>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 font-medium">Nombre:</span>
        <div className="w-[220px]">
          <AutocompleteInput
            placeholder={hasSelection ? `Nombre para ${selectedIds.size} fila${selectedIds.size === 1 ? "" : "s"}` : "Nombre para todos"}
            value={bulkNameText}
            onChange={(e) => setBulkNameText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && bulkNameText.trim()) bulkName(bulkNameText.trim());
            }}
            suggestions={allTypeNames}
            className="h-8 text-sm"
            maxSuggestions={10}
            minCharsToShow={1}
            data-testid="bulk-name-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          disabled={!bulkNameText.trim()}
          onClick={() => bulkName(bulkNameText.trim())}
          data-testid="bulk-name-apply-btn"
        >
          Aplicar
        </Button>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500 font-medium">Vidrio:</span>
        <Input
          className="w-[150px] h-8 text-sm"
          placeholder="Ej: 6mm Templado"
          value={bulkGlassText}
          list="parsed-glass-suggestions"
          onChange={(e) => setBulkGlassText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && bulkGlassText.trim()) bulkGlass(bulkGlassText.trim());
          }}
          data-testid="bulk-glass-input"
        />
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          disabled={!bulkGlassText.trim()}
          onClick={() => bulkGlass(bulkGlassText.trim())}
          data-testid="bulk-glass-apply-btn"
        >
          Aplicar
        </Button>
        <datalist id="parsed-glass-suggestions">
          {glassOptions.map((g) => <option key={g} value={g} />)}
        </datalist>
      </div>
    </div>
  );
});

const ParsedItemsModal = ({
  isOpen,
  onClose,
  parsedItems,
  parsedFilename,
  importAllImages,
  allTypeNames,
  allProjects,
  aluminumSeries,
  quoteTypes,
  materials,
  dimensionUnit,
  setDimensionUnit,
  salesProjectName,
  setSalesProjectName,
  // Bulk update functions
  setAllItemsMaterial,
  setAllItemsColor,
  setAllItemsSeries,
  // Individual item update functions
  updateParsedItemName,
  updateParsedItemImage,
  updateParsedItemMaterial,
  updateParsedItemColor,
  updateParsedItemSeries,
  updateParsedItemGlass,
  setAllItemsGlass,
  availableColors = [],
  availableGlassSpecs = [],
  createCatalogValue,
  updateParsedItemDimensions,
  selectExistingTypeForItem,
  // Series management
  newImportSeries,
  setNewImportSeries,
  fetchMaterials,
  // Save function
  saveParsedItems,
  // API
  API,
  toast
}) => {
  // Calculate validation states
  const itemsWithoutMaterial = parsedItems.filter(i => !i.material).length;
  const itemsWithoutName = parsedItems.filter(i => !i.customName?.trim() && !i.useExistingType).length;
  const isValid = itemsWithoutMaterial === 0 && itemsWithoutName === 0;

  const colorOptions = React.useMemo(() => mergeOptions(DEFAULT_COLORS, availableColors), [availableColors]);
  // Deferred keys: rows only re-render with new suggestions after typing pauses
  const namesKey = React.useDeferredValue(parsedItems.map(i => (i.customName || "").trim()).join("|"));
  const sessionNames = React.useMemo(() => [...new Set(namesKey.split("|").filter(Boolean))], [namesKey]);
  const stable = useStableCallbacks({
    updateParsedItemName, updateParsedItemImage, updateParsedItemMaterial, updateParsedItemColor,
    updateParsedItemSeries, updateParsedItemGlass, updateParsedItemDimensions, selectExistingTypeForItem,
    createCatalogValue,
  });
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const hasSelection = selectedIds.size > 0;
  const allSelected = parsedItems.length > 0 && selectedIds.size === parsedItems.length;
  const toggleItem = React.useCallback((id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(parsedItems.map((i) => i.id)));
  // Bulk setters: apply to the selected rows, or to all rows when nothing is selected
  const applyScoped = (perItemFn, allFn) => (value) => {
    if (hasSelection) selectedIds.forEach((id) => perItemFn(id, value));
    else allFn(value);
  };
  const bulkMaterial = applyScoped(updateParsedItemMaterial, setAllItemsMaterial);
  const bulkColor = applyScoped(updateParsedItemColor, setAllItemsColor);
  const bulkSeries = applyScoped(updateParsedItemSeries, setAllItemsSeries);
  const bulkGlass = applyScoped(updateParsedItemGlass, setAllItemsGlass);
  const bulkName = applyScoped(updateParsedItemName, (v) => parsedItems.forEach((i) => updateParsedItemName(i.id, v)));
  const glassKey = React.useDeferredValue(parsedItems.map(i => i.glass_spec || "").join("|"));
  const glassOptions = React.useMemo(
    () => mergeOptions(availableGlassSpecs, glassKey.split("|")),
    [availableGlassSpecs, glassKey]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Revisar Elementos - {parsedFilename}
          </DialogTitle>
          <DialogDescription>
            Se encontraron {parsedItems.length} elementos. Asigna el nombre del tipo, material, color, serie y tipo de vidrio a cada uno.
          </DialogDescription>
        </DialogHeader>
        
        <BulkQuickActions
          parsedItems={parsedItems}
          selectedIds={selectedIds}
          hasSelection={hasSelection}
          allSelected={allSelected}
          toggleAll={toggleAll}
          clearSelection={() => setSelectedIds(new Set())}
          materials={materials}
          colorOptions={colorOptions}
          aluminumSeries={aluminumSeries}
          glassOptions={glassOptions}
          allTypeNames={allTypeNames}
          createCatalogValue={createCatalogValue}
          bulkMaterial={bulkMaterial}
          bulkColor={bulkColor}
          bulkSeries={bulkSeries}
          bulkGlass={bulkGlass}
          bulkName={bulkName}
        />
        
        {/* Project Name and Image count */}
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-medium">Proyecto:</span>
            <div className="relative">
              <DebouncedInput
                className="w-[200px] h-8 border-blue-300 focus:border-blue-500"
                placeholder="Nombre del Proyecto"
                value={salesProjectName}
                onCommit={setSalesProjectName}
                list="sales-project-suggestions"
                data-testid="sales-project-input"
              />
              <datalist id="sales-project-suggestions">
                {allProjects.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
          </div>
          <div className="text-sm text-slate-500">
            {importAllImages.length} imágenes extraídas del documento
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <ScrollArea className="h-[55vh] border rounded-sm">
            <div className="min-w-[1250px]">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[36px] p-2">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="parsed-header-select-all" />
                    </TableHead>
                    <TableHead className="w-[70px]">Imagen</TableHead>
                    <TableHead className="w-[70px]">Código</TableHead>
                    <TableHead className="w-[200px]">Nombre del Tipo *</TableHead>
                    <TableHead className="text-right w-[80px]">
                      <div className="flex items-center justify-end gap-1">
                        <span>Ancho</span>
                        <Select value={dimensionUnit} onValueChange={setDimensionUnit}>
                          <SelectTrigger className="h-5 w-[50px] text-[10px] px-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mm">mm</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    <TableHead className="text-right w-[80px]">
                      <div className="flex items-center justify-end gap-1">
                        <span>Alto</span>
                        <span className="text-[10px] text-slate-400">({dimensionUnit})</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-right w-[75px]">Costo</TableHead>
                    <TableHead className="text-right w-[65px]">$/m²</TableHead>
                    <TableHead className="w-[110px]">Material *</TableHead>
                    <TableHead className="w-[110px]">Color</TableHead>
                    <TableHead className="w-[110px]">Serie</TableHead>
                    <TableHead className="w-[120px]">Vidrio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedItems.map((item) => (
                    <ParsedItemRow
                      key={item.id}
                      item={item}
                      sessionNames={sessionNames}
                      importAllImages={importAllImages}
                      allTypeNames={allTypeNames}
                      aluminumSeries={aluminumSeries}
                      quoteTypes={quoteTypes}
                      materials={materials}
                      dimensionUnit={dimensionUnit}
                      updateParsedItemName={stable.updateParsedItemName}
                      updateParsedItemImage={stable.updateParsedItemImage}
                      updateParsedItemMaterial={stable.updateParsedItemMaterial}
                      updateParsedItemColor={stable.updateParsedItemColor}
                      updateParsedItemSeries={stable.updateParsedItemSeries}
                      updateParsedItemGlass={stable.updateParsedItemGlass}
                      isSelected={selectedIds.has(item.id)}
                      onToggleSelect={toggleItem}
                      colorOptions={colorOptions}
                      glassOptions={glassOptions}
                      createCatalogValue={stable.createCatalogValue}
                      updateParsedItemDimensions={stable.updateParsedItemDimensions}
                      selectExistingTypeForItem={stable.selectExistingTypeForItem}
                      BACKEND_URL={BACKEND_URL}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>
        </div>
        
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm space-y-1">
            <div className={itemsWithoutMaterial > 0 ? "text-amber-600 font-medium" : "text-emerald-600"}>
              Material: {parsedItems.filter(i => i.material).length} de {parsedItems.length}
            </div>
            <div className={itemsWithoutName > 0 ? "text-amber-600 font-medium" : "text-emerald-600"}>
              Nombre: {parsedItems.filter(i => i.customName?.trim() || i.useExistingType).length} de {parsedItems.length}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={saveParsedItems}
              disabled={!isValid}
              data-testid="save-parsed-items"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Guardar {parsedItems.length} Elementos
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ParsedItemRow - Individual row component for a parsed item
 */
const ParsedItemRow = React.memo(({
  item,
  sessionNames = [],
  importAllImages,
  allTypeNames,
  aluminumSeries,
  quoteTypes,
  materials,
  dimensionUnit,
  updateParsedItemName,
  updateParsedItemImage,
  updateParsedItemMaterial,
  updateParsedItemColor,
  updateParsedItemSeries,
  updateParsedItemGlass,
  isSelected = false,
  onToggleSelect,
  colorOptions = [],
  glassOptions = [],
  createCatalogValue,
  updateParsedItemDimensions,
  selectExistingTypeForItem,
  BACKEND_URL
}) => {
  const isIncomplete = !item.material || (!item.customName?.trim() && !item.useExistingType);
  
  // Handlers for dimension and cost changes
  const handleWidthChange = (e) => {
    let val = parseFloat(e.target.value) || 0;
    // Convert to mm if unit is m
    const width_mm = dimensionUnit === "mm" ? val : val * 1000;
    const height_mm = item.height_mm || 0;
    const area = (width_mm / 1000) * (height_mm / 1000);
    const cost = item.cost || 0;
    const cost_per_m2 = area > 0 ? cost / area : 0;
    updateParsedItemDimensions(item.id, { width_mm, cost_per_m2 });
  };

  const handleHeightChange = (e) => {
    let val = parseFloat(e.target.value) || 0;
    // Convert to mm if unit is m
    const height_mm = dimensionUnit === "mm" ? val : val * 1000;
    const width_mm = item.width_mm || 0;
    const area = (width_mm / 1000) * (height_mm / 1000);
    const cost = item.cost || 0;
    const cost_per_m2 = area > 0 ? cost / area : 0;
    updateParsedItemDimensions(item.id, { height_mm, cost_per_m2 });
  };

  const handleCostChange = (e) => {
    const cost = parseFloat(e.target.value) || 0;
    const width_mm = item.width_mm || 0;
    const height_mm = item.height_mm || 0;
    const area = (width_mm / 1000) * (height_mm / 1000);
    const cost_per_m2 = area > 0 ? cost / area : 0;
    updateParsedItemDimensions(item.id, { cost, cost_per_m2 });
  };
  
  return (
    <TableRow className={`${isIncomplete ? 'bg-amber-50' : ''} ${isSelected ? 'bg-blue-50/60' : ''}`}>
      {/* Select Cell */}
      <TableCell className="p-2 align-middle">
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelect(item.id)} data-testid={`parsed-select-${item.id}`} />
      </TableCell>
      {/* Image Cell */}
      <TableCell className="p-2">
        <Popover>
          <PopoverTrigger asChild>
            <button className="relative group cursor-pointer">
              <ImageThumbnail
                src={item.image_data || (item.image_url ? `${BACKEND_URL}${item.image_url}` : null)}
                alt={item.type_code}
                className="w-12 h-12 rounded border border-slate-200 group-hover:border-blue-400"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded flex items-center justify-center transition-opacity">
                <ImageIcon className="w-4 h-4 text-white" />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-2" side="right">
            <div className="space-y-2">
              <div className="flex items-center justify-between border-b pb-2">
                <p className="text-sm font-medium">Seleccionar Imagen para {item.type_code}</p>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => updateParsedItemImage(item.id, null)}
                  className="text-red-600 h-7"
                >
                  Sin imagen
                </Button>
              </div>
              <ScrollArea className="h-[200px]">
                <div className="grid grid-cols-4 gap-2">
                  {importAllImages.map((imgData, idx) => (
                    <button
                      key={`import-img-${idx}-${imgData.slice(-20)}`}
                      onClick={() => updateParsedItemImage(item.id, imgData)}
                      className={`border rounded p-1 hover:border-blue-400 transition-colors ${
                        item.image_data === imgData ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'
                      }`}
                    >
                      <img 
                        src={imgData} 
                        alt={`Imagen ${idx + 1}`}
                        className="w-full h-16 object-contain"
                      />
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </PopoverContent>
        </Popover>
      </TableCell>
      
      {/* Code Cell */}
      <TableCell className="font-mono text-xs p-2">
        {item.type_code}
      </TableCell>
      
      {/* Name Cell */}
      <TableCell className="p-2">
        <div className="flex flex-col gap-1">
          {/* Match confidence badge */}
          {item.matchConfidence && item.useExistingType && (
            <MatchConfidenceBadge 
              confidence={item.matchConfidence} 
              typeName={item.customName}
            />
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                data-testid={`item-name-trigger-${item.id}`}
                className={`w-full justify-start text-left font-normal h-9 ${
                  item.useExistingType 
                    ? item.matchConfidence === 'low' || item.matchConfidence === 'medium'
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-emerald-50 border-emerald-300'
                    : ''
                }`}
              >
                {item.useExistingType ? (
                  <><Link className="w-3 h-3 mr-1 text-emerald-600" /><span className="truncate">{item.customName}</span></>
                ) : item.customName ? (
                  <><Edit3 className="w-3 h-3 mr-1" /><span className="truncate">{item.customName}</span></>
                ) : (
                  <span className="text-amber-600">Nombre requerido...</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <div className="p-2 border-b">
                <DebouncedAutocomplete
                  placeholder="Nombre del tipo (ej: Mampara 2 hojas)"
                  data-testid={`item-name-input-${item.id}`}
                  value={item.customName || ""}
                  onCommit={(v) => updateParsedItemName(item.id, v)}
                  suggestions={allTypeNames}
                  className="h-8"
                  maxSuggestions={10}
                  minCharsToShow={1}
                />
              </div>
              
              {/* Suggestions from current session */}
              {(() => {
                const currentNames = sessionNames.filter(n => n !== (item.customName || "").trim());
                const searchTerm = (item.customName || "").toLowerCase();
                const filteredNames = searchTerm 
                  ? currentNames.filter(n => n.toLowerCase().includes(searchTerm))
                  : currentNames;
                
                if (filteredNames.length > 0) {
                  return (
                    <>
                      <div className="p-2 border-b bg-emerald-50">
                        <span className="text-xs text-emerald-700 font-medium">Nombres usados en esta importación:</span>
                      </div>
                      <div className="max-h-[120px] overflow-y-auto">
                        {filteredNames.map((name) => (
                          <button
                            key={`name-${name}`}
                            onClick={() => updateParsedItemName(item.id, name)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2 border-b border-slate-100"
                          >
                            <Edit3 className="w-3 h-3 text-emerald-600" />
                            <span className="truncate">{name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  );
                }
                return null;
              })()}
              
              {/* Existing types from database */}
              <div className="p-2 border-b bg-slate-50">
                <span className="text-xs text-slate-500 font-medium">O selecciona un tipo existente:</span>
              </div>
              <ScrollArea className="h-[150px]">
                <div className="p-1">
                  {(() => {
                    const searchTerm = (item.customName || "").toLowerCase().trim();
                    // Filter types based on search term
                    const filteredExistingTypes = searchTerm
                      ? quoteTypes.filter(type => 
                          type.name.toLowerCase().includes(searchTerm) ||
                          (type.material && type.material.toLowerCase().includes(searchTerm)) ||
                          (type.series && type.series.toLowerCase().includes(searchTerm))
                        )
                      : quoteTypes;
                    
                    // Sort: matched first, then alphabetically
                    const sortedTypes = [...filteredExistingTypes].sort((a, b) => {
                      if (searchTerm) {
                        const aExact = a.name.toLowerCase() === searchTerm;
                        const bExact = b.name.toLowerCase() === searchTerm;
                        if (aExact && !bExact) return -1;
                        if (!aExact && bExact) return 1;
                        
                        const aStarts = a.name.toLowerCase().startsWith(searchTerm);
                        const bStarts = b.name.toLowerCase().startsWith(searchTerm);
                        if (aStarts && !bStarts) return -1;
                        if (!aStarts && bStarts) return 1;
                      }
                      return a.name.localeCompare(b.name);
                    });
                    
                    if (quoteTypes.length === 0) {
                      return <p className="text-sm text-slate-400 p-2">No hay tipos registrados</p>;
                    }
                    
                    if (sortedTypes.length === 0) {
                      return (
                        <div className="p-2 text-center" data-testid={`no-type-matches-${item.id}`}>
                          <p className="text-sm text-slate-400">No hay coincidencias para &quot;{item.customName}&quot;</p>
                          <p className="text-xs text-slate-400 mt-1">Se creará un nuevo tipo</p>
                        </div>
                      );
                    }
                    
                    return sortedTypes.map((type) => (
                      <button
                        key={type.id}
                        data-testid={`existing-type-option-${type.id}`}
                        onClick={() => selectExistingTypeForItem(item.id, type)}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 flex items-center gap-2 ${item.useExistingType === type.id ? 'bg-blue-100' : ''}`}
                      >
                        {type.image_data ? (
                          <img 
                            src={type.image_data}
                            alt={type.name}
                            className="w-8 h-8 object-contain rounded border"
                          />
                        ) : type.image_url ? (
                          <img 
                            src={`${BACKEND_URL}${type.image_url}`}
                            alt={type.name}
                            className="w-8 h-8 object-contain rounded border"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-slate-100 rounded border flex items-center justify-center">
                            <Square className="w-4 h-4 text-slate-300" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" title={type.name}>{type.name}</p>
                          <p className="text-xs text-slate-500" title={`${type.material}${type.series ? ` - ${type.series}` : ''}${type.color ? ` - ${type.color}` : ''}`}>
                            {type.material} {type.series && `- ${type.series}`} {type.color && `- ${type.color}`}
                          </p>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>
      </TableCell>
      
      {/* Width Cell - Editable */}
      <TableCell className="p-1">
        <Input
          type="number"
          step={dimensionUnit === "mm" ? "1" : "0.001"}
          value={dimensionUnit === "mm" ? (item.width_mm || "") : ((item.width_mm || 0) / 1000).toFixed(3)}
          onChange={handleWidthChange}
          className="h-8 text-xs font-mono w-[75px] text-right"
          data-testid={`width-input-${item.id}`}
        />
      </TableCell>
      
      {/* Height Cell - Editable */}
      <TableCell className="p-1">
        <Input
          type="number"
          step={dimensionUnit === "mm" ? "1" : "0.001"}
          value={dimensionUnit === "mm" ? (item.height_mm || "") : ((item.height_mm || 0) / 1000).toFixed(3)}
          onChange={handleHeightChange}
          className="h-8 text-xs font-mono w-[75px] text-right"
          data-testid={`height-input-${item.id}`}
        />
      </TableCell>
      
      {/* Cost Cell - Editable */}
      <TableCell className="p-1">
        <Input
          type="number"
          step="0.01"
          value={item.cost || ""}
          onChange={handleCostChange}
          className="h-8 text-xs font-mono w-[80px] text-right font-medium"
          data-testid={`cost-input-${item.id}`}
        />
      </TableCell>
      
      {/* Cost per m² Cell */}
      <TableCell className="text-right font-mono text-xs p-2">
        {formatCurrency(item.cost_per_m2)}
      </TableCell>
      
      {/* Material Cell */}
      <TableCell className="p-2">
        <CreatableSelect
          value={item.material || ""}
          onChange={(v) => updateParsedItemMaterial(item.id, v)}
          options={materials}
          placeholder="Seleccionar"
          createLabel="Nuevo material…"
          onCreate={(name) => createCatalogValue("material", name)}
          className={`w-full ${!item.material ? "border-amber-400" : ""}`}
          testId={`material-select-${item.id}`}
        />
      </TableCell>
      
      {/* Color Cell */}
      <TableCell className="p-2">
        <CreatableSelect
          value={item.color || ""}
          onChange={(v) => updateParsedItemColor(item.id, v)}
          options={colorOptions}
          placeholder="Color..."
          createLabel="Nuevo color…"
          onCreate={(name) => createCatalogValue("color", name)}
          className="w-full"
          testId={`color-select-${item.id}`}
        />
      </TableCell>
      
      {/* Series Cell */}
      <TableCell className="p-2">
        <CreatableSelect
          value={item.series || ""}
          onChange={(v) => updateParsedItemSeries(item.id, v)}
          options={aluminumSeries}
          placeholder="Serie..."
          createLabel="Nueva serie…"
          onCreate={(name) => createCatalogValue("series", name)}
          className="w-full"
          testId={`series-select-${item.id}`}
        />
      </TableCell>
      
      {/* Glass Cell */}
      <TableCell className="p-2">
        <DebouncedInput
          value={item.glass_spec || ""}
          onCommit={(v) => updateParsedItemGlass(item.id, v)}
          placeholder="Vidrio..."
          list="parsed-glass-suggestions"
          className="w-full h-8 text-xs"
          data-testid={`glass-input-${item.id}`}
        />
      </TableCell>
    </TableRow>
  );
});

export { ParsedItemsModal, ParsedItemRow };
export default ParsedItemsModal;
